import { ControllerObtenerProcedimientosPendientes, ControllerProcedimientosPrecios } from "../Service/Service.porcedimientos";
import { ListadoPromocion } from "../Service/Service.promocion";

/* ============================================================================
   PANEL DE AGREGAR · FASE 4 — caché en memoria y precarga

   POR QUÉ EXISTE: en cuanto se elige el paciente ya se sabe qué se va a
   necesitar -- sus pendientes y el tarifario de su cobertura. Pedirlo EN ESE
   MOMENTO, en segundo plano, hace que al pulsar "Agregar procedimientos" los
   datos ya estén. Elimina casi toda la espera percibida sin cambiar ni un
   endpoint.

   ES SÓLO MEMORIA, a propósito: un `Map` que se vacía al recargar la página.
   No hay localStorage ni nada persistente -- un tarifario o una lista de
   pendientes cacheados entre sesiones serían datos viejos con pinta de
   nuevos, y aquí se factura con ellos.

   TRAMPA 4 del encargo: precargar NO PUEDE BLOQUEAR. `precargar` no devuelve
   nada que haya que esperar, se traga sus propios errores y nadie mira su
   resultado. Si falla o tarda, el panel se abre igual y pide lo que le falte.
   ========================================================================== */

/* Sólo los últimos paciente/seguro. Caja no atiende a cien pacientes a la vez,
   y un `Map` sin tope es una fuga de memoria con 6.847 KB por entrada. */
const TOPE = 3;

const cache = new Map();

export function claveDe(pacienteId, seguroId) {
    return `${pacienteId ?? ""}|${seguroId ?? ""}`;
}

function entrada(clave) {
    if (!cache.has(clave)) cache.set(clave, {});
    return cache.get(clave);
}

function podarCache() {
    while (cache.size > TOPE) {
        // `Map` conserva el orden de inserción: el primero es el más viejo.
        const masVieja = cache.keys().next().value;
        cache.delete(masVieja);
    }
}

/** Lo cacheado, o `undefined` si nunca se pidió. `null` NO es un valor válido. */
export function leer(clave, cual) {
    return cache.get(clave)?.[cual];
}

export function guardar(clave, cual, datos) {
    entrada(clave)[cual] = datos;
    podarCache();
}

/**
 * Invalida una lista (o la entrada entera).
 *
 * Hace falta de verdad en un caso concreto: al agregar del catálogo o de una
 * promoción se CREAN prestaciones en la base, así que la lista de pendientes
 * que teníamos cacheada acaba de quedarse vieja. Sin esto, abrir Pendientes
 * después de agregar del catálogo mostraría una lista sin esas filas.
 */
export function invalidar(clave, cual) {
    if (!cual) {
        cache.delete(clave);
        return;
    }
    const e = cache.get(clave);
    if (e) delete e[cual];
}

/** Sólo para las pruebas: deja la caché como recién arrancada. */
export function limpiarCache() {
    cache.clear();
}

/* -- las tres cargas, en un solo sitio -------------------------------------
   Las usa el panel Y la precarga, así que la forma de interpretar cada
   respuesta se decide una vez. Ninguna cambia el endpoint ni sus argumentos.
   ------------------------------------------------------------------------- */

/**
 * El endpoint de pendientes responde `success:false` con "No hay
 * procedimientos registrados" cuando la lista está vacía. Eso NO es un error:
 * es un paciente sin nada por cobrar.
 */
const esVacioLegitimo = (mensaje) =>
    /no hay procedimientos|no hay examenes/i.test(String(mensaje || ""));

export async function traerPendientes(pacienteId, seguroId) {
    const res = await ControllerObtenerProcedimientosPendientes(pacienteId, seguroId);
    if (res && res.success) return { datos: res.data || [], error: null };
    return {
        datos: [],
        error: esVacioLegitimo(res?.message)
            ? null
            : res?.message || "No se pudieron obtener los pendientes.",
    };
}

export async function traerCatalogo(seguroId) {
    const res = await ControllerProcedimientosPrecios(seguroId || undefined);
    if (res && res.success) {
        // Sin `idseguro` el servidor devuelve TODAS las aseguradoras, así que
        // el filtro local se conserva como red de seguridad: si el paciente no
        // tuviera seguro resuelto, la lista seguiría siendo la correcta en vez
        // de mezclar tarifarios.
        const filas = seguroId ? (res.data || []).filter((x) => x.seguro_id === seguroId) : res.data || [];
        return { datos: filas, error: null };
    }
    return {
        datos: [],
        error: esVacioLegitimo(res?.message) ? null : res?.message || "No se pudo obtener el catálogo.",
    };
}

export async function traerPromociones(seguroId) {
    const res = await ListadoPromocion(seguroId ? { seguro_id: seguroId } : {});
    if (res && res.success) return { datos: res.data || [], error: null };
    return { datos: [], error: res?.message || "No se pudieron obtener las promociones." };
}

/**
 * Precarga en segundo plano, al elegir el paciente.
 *
 * Sólo pendientes y catálogo: son las dos pestañas que se usan de verdad en
 * cada factura. Las promociones (56 activas, 126 KB) se piden al abrir su
 * pestaña -- precargar algo que casi nunca se abre es gastar la red del
 * usuario en nuestro nombre.
 *
 * OJO A LOS DOS "SEGUROS", que no son el mismo y confundirlos trae una lista
 * equivocada:
 *
 *   `seguroId`         -> la aseguradora elegida como TITULAR de la factura
 *                         (facturar a la aseguradora, sin paciente). Es el
 *                         `idseguro` de /cita_procedimiento_pendientes, y con
 *                         él el backend hace una consulta DISTINTA: filtra por
 *                         `seguro_id` y `factura_seguro = 1`, o sea las
 *                         responsabilidades de la aseguradora, no las del
 *                         paciente.
 *   `seguroPacienteId` -> la COBERTURA del paciente. Es lo que decide qué
 *                         tarifario y qué promociones le corresponden.
 *
 * La clave de la caché se construye con `seguroId`, igual que en el panel, para
 * que las dos miren la misma casilla.
 *
 * Si algo ya está cacheado no se vuelve a pedir.
 */
export function precargar({ pacienteId, seguroId, seguroPacienteId }) {
    if (!pacienteId && !seguroId) return;
    const clave = claveDe(pacienteId, seguroId);

    if (leer(clave, "pendientes") === undefined) {
        // Se marca ANTES del await, para que abrir el panel en ese instante no
        // dispare una segunda petición de la misma cosa.
        guardar(clave, "pendientes", null);
        traerPendientes(pacienteId, seguroId)
            .then(({ datos, error }) => {
                // Un fallo en la precarga se descarta y se deja la casilla
                // libre: que el panel lo pida y ENSEÑE el error cuando el
                // usuario esté mirando, no en silencio de fondo.
                if (error) invalidar(clave, "pendientes");
                else guardar(clave, "pendientes", datos);
            })
            .catch(() => invalidar(clave, "pendientes"));
    }

    if (pacienteId && leer(clave, "catalogo") === undefined) {
        guardar(clave, "catalogo", null);
        traerCatalogo(seguroPacienteId)
            .then(({ datos, error }) => {
                if (error) invalidar(clave, "catalogo");
                else guardar(clave, "catalogo", datos);
            })
            .catch(() => invalidar(clave, "catalogo"));
    }
}
